// src/redux/slices/clipsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API_ROUTES from '../../config/apiRoutes';

// Async thunk to fetch all clips
export const fetchClips = createAsyncThunk(
  'clips/fetchClips',
  async (params = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const { currentPage = 1, limit = 10 } = params;
      
      const payload = {
        currentPage,
        limit
      };

      console.log('Clips API Request Payload:', payload);

      const response = await fetch(API_ROUTES.CLIPS.GET_ALL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clips');
      }

      const responseData = await response.json();
      
      console.log('Clips API Response:', responseData);

      // Handle different possible response structures (same as auction)
      let clips = [];
      let total = 0;
      let totalPages = 1;

      if (responseData.data && Array.isArray(responseData.data)) {
        clips = responseData.data;
        total = responseData.total || responseData.data.length;
        totalPages = responseData.totalPages || Math.ceil(total / payload.limit);
      } else if (Array.isArray(responseData)) {
        clips = responseData;
        // If we get a direct array, assume there are more pages if we got exactly the limit
        if (clips.length === payload.limit) {
          // Estimate total pages - this is a fallback when API doesn't provide pagination info
          totalPages = Math.max(5, Math.ceil(clips.length * 1.5 / payload.limit));
          total = totalPages * payload.limit;
        } else {
          // If we got fewer clips than requested, this is likely the last page
          totalPages = payload.currentPage;
          total = clips.length + (payload.currentPage - 1) * payload.limit;
        }
      } else if (responseData.clips && Array.isArray(responseData.clips)) {
        clips = responseData.clips;
        total = responseData.total || responseData.clips.length;
        totalPages = responseData.totalPages || Math.ceil(total / payload.limit);
      }

      // Ensure we always have at least some pagination if we have clips
      if (clips.length > 0 && totalPages < 1) {
        totalPages = Math.max(1, Math.ceil(clips.length / payload.limit));
        total = Math.max(clips.length, total);
      }

      console.log('Processed clips data:', { clips: clips.length, total, totalPages });

      return {
        clips,
        total,
        totalPages,
        currentPage: payload.currentPage
      };
    } catch (error) {
      console.error('Clips API Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

const clipsSlice = createSlice({
  name: 'clips',
  initialState: {
    clips: [],
    loading: false,
    error: null,
    total: 0,
    totalPages: 0,
    currentPage: 1,
  },
  reducers: {
    clearClipsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClips.fulfilled, (state, action) => {
        state.loading = false;
        state.clips = action.payload.clips;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchClips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearClipsError } = clipsSlice.actions;
export default clipsSlice.reducer;